import { useEffect, useMemo, useState } from 'react';
import { apiRequest, TOKEN_KEY } from '../../api/client';
import { Button, Checkbox, Stack, Typography, TextField } from '@mui/material';
export const AUTO_VISIT_DEFAULTS = {
  enabled: true,
  dwellMs: 60000,
  cooldownMs: 7200000,
  verifyRequiredSamples: 5,
  verifyMinInsideSamples: 3,
  verifyMaxAccuracyMeters: 25,
  verifyIntervalMs: 2500,
  verifyTimeoutMs: 25000,
  verifyPolygonBufferMeters: 12,
  verifyMaxSpeedMps: 3.5,
  retryEnabled: true,
  retryDelayMs: 120000,
  retryMaxAttempts: 5,
  retryMaxWindowMs: 900000,
  areaExitRadiusMeters: 5000,
  maxCandidatePlaces: 15,
  geofenceNotificationResponsivenessMs: 30000,
  areaExitNotificationResponsivenessMs: 60000,
  refreshLockMs: 60000,
  sessionStartLockMs: 120000,
};
const fields = [
  ['כללי', 'dwellMs', 'זמן DWELL', 1000, 'שניות', 'משך השהייה בתוך ה־Geofence לפני אימות GPS.'],
  ['כללי', 'cooldownMs', 'זמן Cooldown', 60000, 'דקות', 'זמן מינימלי בין דיווחים לאותו מקום.'],
  [
    'אימות מיקום',
    'verifyRequiredSamples',
    'מספר דגימות נדרש',
    1,
    'דגימות',
    'כמות דגימות GPS מדויקות שנדרשות להחלטה.',
  ],
  [
    'אימות מיקום',
    'verifyMinInsideSamples',
    'מינימום בתוך המקום',
    1,
    'דגימות',
    'כמה מהדגימות חייבות להיות בתוך המקום.',
  ],
  [
    'אימות מיקום',
    'verifyMaxAccuracyMeters',
    'דיוק GPS מקסימלי',
    1,
    'מטרים',
    'דגימות עם דיוק גרוע יותר יידחו.',
  ],
  ['אימות מיקום', 'verifyIntervalMs', 'מרווח בין דגימות', 1000, 'שניות', 'ההמתנה בין בקשות מיקום.'],
  [
    'אימות מיקום',
    'verifyTimeoutMs',
    'Timeout לאימות',
    1000,
    'שניות',
    'משך הזמן המקסימלי של ניסיון.',
  ],
  [
    'אימות מיקום',
    'verifyPolygonBufferMeters',
    'Buffer לפוליגון',
    1,
    'מטרים',
    'הרחבה להתמודדות עם סטיות GPS.',
  ],
  [
    'אימות מיקום',
    'verifyMaxSpeedMps',
    'מהירות מקסימלית',
    1,
    'מ׳/שנייה',
    'מהירות גבוהה עשויה להעיד על מעבר ליד המקום.',
  ],
  [
    'Retry',
    'retryDelayMs',
    'המתנה בין ניסיונות',
    60000,
    'דקות',
    'כמה זמן להמתין לפני ניסיון נוסף.',
  ],
  [
    'Retry',
    'retryMaxAttempts',
    'מספר ניסיונות מקסימלי',
    1,
    'ניסיונות',
    'כמה ניסיונות יתבצעו לכל DWELL.',
  ],
  [
    'Retry',
    'retryMaxWindowMs',
    'חלון זמן מקסימלי',
    60000,
    'דקות',
    'כמה זמן שרשרת האימות יכולה להישאר פעילה.',
  ],
  [
    'Geofence',
    'areaExitRadiusMeters',
    'רדיוס AREA_EXIT',
    1,
    'מטרים',
    'יציאה מהרדיוס מפעילה רענון מועמדים.',
  ],
  [
    'Geofence',
    'maxCandidatePlaces',
    'מספר מועמדים מקסימלי',
    1,
    'מקומות',
    'מספר המקומות המרבי בכל רענון.',
  ],
  [
    'Geofence',
    'geofenceNotificationResponsivenessMs',
    'זמן תגובת Geofence',
    1000,
    'שניות',
    'חלון תגובה לאירועי מקום.',
  ],
  [
    'Geofence',
    'areaExitNotificationResponsivenessMs',
    'זמן תגובת AREA_EXIT',
    1000,
    'שניות',
    'חלון תגובה לאירוע היציאה.',
  ],
  ['Geofence', 'refreshLockMs', 'נעילת רענון', 1000, 'שניות', 'מונע רענונים מקבילים.'],
  [
    'כללי',
    'sessionStartLockMs',
    'נעילת התחלת Session',
    1000,
    'שניות',
    'מונע התחלת sessions מקבילים.',
  ],
];
const options = (value = {}) => ({
  ...value,
  headers: {
    Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY) || ''}`,
    ...(value.headers || {}),
  },
});
export default function AutoVisitConfigSection() {
  const [draft, setDraft] = useState(null),
    [meta, setMeta] = useState({}),
    [busy, setBusy] = useState(true),
    [message, setMessage] = useState('');
  useEffect(() => {
    apiRequest('/bo/auto-visit-config', options())
      .then((data) => {
        const { version, updatedAt, updatedBy, ...config } = data;
        setDraft(config);
        setMeta({
          version,
          updatedAt,
          updatedBy,
        });
      })
      .catch((e) => setMessage(e.message))
      .finally(() => setBusy(false));
  }, []);
  const invalid = useMemo(
    () =>
      !draft ||
      draft.verifyMinInsideSamples > draft.verifyRequiredSamples ||
      draft.retryMaxWindowMs < draft.retryDelayMs ||
      draft.verifyTimeoutMs <
        draft.verifyIntervalMs * Math.max(1, draft.verifyRequiredSamples - 1) ||
      fields.some(([, k]) => !Number.isFinite(Number(draft[k])) || Number(draft[k]) < 0),
    [draft],
  );
  if (busy)
    return (
      <Stack
        sx={{
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: '16px',
          padding: '20px',
        }}
      >
        <Typography>דיווח אוטומטי</Typography>
        <Typography>טוען הגדרות…</Typography>
      </Stack>
    );
  if (!draft)
    return (
      <Stack
        sx={{
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: '16px',
          padding: '20px',
        }}
      >
        <Typography>דיווח אוטומטי</Typography>
        <Typography
          sx={{
            borderRightColor: '#f79009',
            background: '#fffaeb',
            color: '#b54708',
          }}
        >
          {message}
        </Typography>
      </Stack>
    );
  const update = (key, value) =>
    setDraft((current) => ({
      ...current,
      [key]: value,
    }));
  const save = async () => {
    if (!window.confirm('שינויים בשדות Geofence עשויים לגרום לרישום מחדש. לשמור?')) return;
    setBusy(true);
    setMessage('');
    try {
      const data = await apiRequest(
        '/bo/auto-visit-config',
        options({
          method: 'PUT',
          body: JSON.stringify(draft),
        }),
      );
      const { version, updatedAt, updatedBy, ...config } = data;
      setDraft(config);
      setMeta({
        version,
        updatedAt,
        updatedBy,
      });
      setMessage('ההגדרות נשמרו');
    } catch (e) {
      setMessage(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Stack
      sx={(theme) => ({
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: '16px',
        padding: '20px',
        [theme.breakpoints.down('sm')]: {
          padding: '14px',
        },
      })}
    >
      <Stack
        sx={{
          display: 'flex',
          alignItems: 'end',
          justifyContent: 'space-between',
          gap: '12px',
          flexWrap: 'wrap',
        }}
      >
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
          }}
        >
          <Checkbox checked={draft.enabled} onChange={(e) => update('enabled', e.target.checked)} />
          <Typography>דיווח אוטומטי</Typography>
        </Stack>
      </Stack>

      {['כללי', 'אימות מיקום', 'Retry', 'Geofence'].map((group) => (
        <Stack key={group}>
          <Typography>{group}</Typography>
          <Stack
            sx={(theme) => ({
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(190px, 1fr))',
              gap: '14px',
              margin: '12px 0 20px',
              '& > label': {
                display: 'grid',
                gap: '6px',
              },
              [theme.breakpoints.down('md')]: {
                gridTemplateColumns: '1fr',
              },
              [theme.breakpoints.down('sm')]: {
                gridTemplateColumns: '1fr',
              },
            })}
          >
            {fields
              .filter(([g]) => g === group)
              .map(([, key, label, divisor, unit, help]) => (
                <Stack key={key}>
                  {label}
                  <Typography
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <TextField
                      type="number"
                      step="any"
                      value={draft[key] / divisor}
                      onChange={(e) => update(key, Number(e.target.value) * divisor)}
                      fullWidth
                    />
                    <Typography>{unit}</Typography>
                  </Typography>
                  <Typography
                    sx={(theme) => ({
                      color: 'var(--muted)',
                      [theme.breakpoints.down('lg')]: {
                        margin: '4px 0 0',
                        fontSize: '12px',
                        display: 'none',
                      },
                      margin: '2px 0 0',
                      fontSize: '11px',
                    })}
                  >
                    {help}
                  </Typography>
                  {group === 'Geofence' && (
                    <Typography
                      sx={{
                        borderRightColor: '#f79009',
                        background: '#fffaeb',
                        color: '#b54708',
                      }}
                    >
                      שינוי דורש רישום מחדש
                    </Typography>
                  )}
                </Stack>
              ))}

            {group === 'Retry' && (
              <Stack
                direction="row"
                sx={{
                  alignItems: 'center',
                }}
              >
                <Checkbox
                  checked={draft.retryEnabled}
                  onChange={(e) => update('retryEnabled', e.target.checked)}
                />
                הפעלת Retry
              </Stack>
            )}
          </Stack>
        </Stack>
      ))}

      <Stack
        sx={{
          padding: '12px',
          borderRadius: '10px',
          background: '#f1f5f9',
          marginBottom: '12px',
        }}
      >
        גרסה {meta.version || 1} · עודכן{' '}
        {meta.updatedAt ? new Date(meta.updatedAt).toLocaleString('he-IL') : 'טרם נשמר'}
      </Stack>

      {invalid && (
        <Typography
          sx={{
            borderRightColor: '#f79009',
            background: '#fffaeb',
            color: '#b54708',
          }}
        >
          יש לתקן ערכים לא חוקיים לפני השמירה.
        </Typography>
      )}

      {message && <Typography>{message}</Typography>}
      <Stack
        direction="row"
        sx={{
          gap: '10px',
          justifyContent: 'end',
        }}
      >
        <Button
          type="button"
          onClick={() =>
            setDraft({
              ...AUTO_VISIT_DEFAULTS,
            })
          }
          sx={{
            background: '#edf2f7',
            color: 'var(--text)',
            '&:hover': {
              background: '#ddebff',
              color: '#1269e8',
            },
          }}
        >
          איפוס לערכי ברירת מחדל
        </Button>

        <Button type="button" disabled={busy || invalid} onClick={save}>
          {busy ? 'שומר…' : 'שמירת דיווח אוטומטי'}
        </Button>
      </Stack>
    </Stack>
  );
}
